import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RepeatOutlinedIcon from "@mui/icons-material/RepeatOutlined";
import { AppColors } from "../../constants/AppColors";
import { ExpenseResponse } from "../../models/ResponseModels/ExpenseResponse";
import { RecurringExpenseResponse } from "../../models/ResponseModels/RecurringExpenseResponse";
import {
  formatMoney,
  getExpenseLocation,
  getRecurringExpenseLocation,
} from "./statisticsHelpers";

interface StatisticsExpenseListsProps {
  expenses: ExpenseResponse[];
  recurringExpenses: RecurringExpenseResponse[];
  openExpenseModal: (expense?: ExpenseResponse) => void;
  openRecurringExpenseModal: (expense?: RecurringExpenseResponse) => void;
  confirmDeleteExpense: (expense: ExpenseResponse) => void;
  confirmDeleteRecurringExpense: (expense: RecurringExpenseResponse) => void;
  t: (key: string, values?: Record<string, string | number>) => string;
}

const StatisticsExpenseLists = ({
  expenses,
  recurringExpenses,
  openExpenseModal,
  openRecurringExpenseModal,
  confirmDeleteExpense,
  confirmDeleteRecurringExpense,
  t,
}: StatisticsExpenseListsProps) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
        gap: 2,
      }}
    >
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography sx={{ fontWeight: 950 }}>
              {t("expenses.manualExpenses")}
            </Typography>

            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => openExpenseModal()}
            >
              {t("expenses.newExpense")}
            </Button>
          </Stack>

          <Divider sx={{ mb: 1 }} />

          {expenses.length === 0 ? (
            <Typography color="text.secondary">
              {t("expenses.noExpenses")}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {expenses.map((expense) => (
                <Box
                  key={expense.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${AppColors.border}`,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", sm: "center" },
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 900 }}>
                        {expense.name}
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        {getExpenseLocation(expense)} ·{" "}
                        {expense.expense_date?.split("T")[0]}
                        {expense.category ? ` · ${expense.category}` : ""}
                      </Typography>

                      {expense.notes ? (
                        <Typography variant="body2" color="text.secondary">
                          {expense.notes}
                        </Typography>
                      ) : null}
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: "center" }}
                    >
                      <Typography sx={{ fontWeight: 900 }}>
                        {formatMoney(expense.amount, expense.currency)}
                      </Typography>

                      <Tooltip title={t("common.edit")}>
                        <IconButton
                          size="small"
                          onClick={() => openExpenseModal(expense)}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={t("common.delete")}>
                        <IconButton
                          size="small"
                          onClick={() => confirmDeleteExpense(expense)}
                        >
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography sx={{ fontWeight: 950 }}>
              {t("expenses.recurringExpenses")}
            </Typography>

            <Button
              size="small"
              startIcon={<RepeatOutlinedIcon />}
              onClick={() => openRecurringExpenseModal()}
            >
              {t("expenses.newRecurringExpense")}
            </Button>
          </Stack>

          <Divider sx={{ mb: 1 }} />

          {recurringExpenses.length === 0 ? (
            <Typography color="text.secondary">
              {t("expenses.noRecurringExpenses")}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {recurringExpenses.map((expense) => (
                <Box
                  key={expense.id}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${AppColors.border}`,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    sx={{
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", sm: "center" },
                    }}
                  >
                    <Box>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography sx={{ fontWeight: 900 }}>
                          {expense.name}
                        </Typography>

                        <Chip
                          size="small"
                          label={
                            expense.active
                              ? t("expenses.active")
                              : t("expenses.inactive")
                          }
                          color={expense.active ? "success" : "default"}
                        />
                      </Stack>

                      <Typography variant="body2" color="text.secondary">
                        {getRecurringExpenseLocation(expense)} ·{" "}
                        {expense.frequency} ·{" "}
                        {expense.category || t("expenses.uncategorized")}
                      </Typography>

                      {expense.notes ? (
                        <Typography variant="body2" color="text.secondary">
                          {expense.notes}
                        </Typography>
                      ) : null}
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: "center" }}
                    >
                      <Typography sx={{ fontWeight: 900 }}>
                        {formatMoney(expense.amount, expense.currency)}
                      </Typography>

                      <Tooltip title={t("common.edit")}>
                        <IconButton
                          size="small"
                          onClick={() => openRecurringExpenseModal(expense)}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={t("common.delete")}>
                        <IconButton
                          size="small"
                          onClick={() => confirmDeleteRecurringExpense(expense)}
                        >
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default StatisticsExpenseLists;
